import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Edit, Trash2, Users, Tag, Calendar, Settings, Award, BookOpen, ArrowLeft, ChevronRight, CheckCircle, XCircle, RotateCcw, X } from 'lucide-react';
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { ModuleForm } from "./ModuleForm";
import { CertificateTemplatesManager } from '@/components/training/CertificateTemplatesManager';

interface TrainingCategory {
  id: string;
  name_en: string;
  name_el: string;
  description_en?: string;
  description_el?: string;
  color: string;
  icon: string;
}
interface TrainingModule {
  id: string;
  title_en: string;
  title_el: string;
  category_id?: string;
  training_categories?: TrainingCategory;
  category?: string;
  content_en?: any;
  content_el?: any;
  description_en?: string;
  description_el?: string;
  duration_minutes?: number;
  difficulty_level?: string;
  is_active?: boolean;
  created_at?: string;
  created_by?: string;
  updated_at?: string;
  tags?: string[];
}
interface Team {
  id: string;
  name: string;
}
interface Assignment {
  id: string;
  module_id: string;
  team_id?: string;
  user_id?: string;
  assigned_at: string;
  due_date?: string;
  is_mandatory: boolean;
  training_modules: TrainingModule;
  teams?: Team;
}
export const TrainingAdminPanel = () => {
  const navigate = useNavigate();
  const {
    language
  } = useLanguage();
  const [categories, setCategories] = useState<TrainingCategory[]>([]);
  const [modules, setModules] = useState<TrainingModule[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [editingCategory, setEditingCategory] = useState<TrainingCategory | null>(null);
  const [editingModule, setEditingModule] = useState<TrainingModule | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isModuleDialogOpen, setIsModuleDialogOpen] = useState(false);
  const [isAssignmentDialogOpen, setIsAssignmentDialogOpen] = useState(false);
  const [isAssignmentDetailsOpen, setIsAssignmentDetailsOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [assignmentMembers, setAssignmentMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    fetchData();
  }, []);
  const fetchData = async () => {
    try {
      setLoading(true);

      // Get current user and check if super admin
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }
      
      const { data: isSuperAdminData } = await supabase.rpc('is_super_admin', { user_id: user.id });
      const isSuperAdmin = isSuperAdminData === true;
      
      // Get admin bubble IDs
      let bubbleAdminIds = [user.id];
      if (!isSuperAdmin) {
        const { data: contributors } = await supabase
          .from('admin_contributors')
          .select('contributor_admin_id')
          .eq('parent_admin_id', user.id);
        
        bubbleAdminIds = [user.id, ...(contributors?.map(c => c.contributor_admin_id) || [])];
      }

      // Fetch categories (filtered by bubble)
      let categoriesQuery = supabase.from('training_categories').select('*').order('name_en');
      if (!isSuperAdmin) {
        categoriesQuery = categoriesQuery.in('created_by', bubbleAdminIds);
      }
      const { data: categoriesData, error: categoriesError } = await categoriesQuery;
      if (categoriesError) throw categoriesError;
      setCategories(categoriesData || []);

      // Fetch modules with categories (filtered by bubble)
      let modulesQuery = supabase.from('training_modules').select(`
          *,
          training_categories (*)
        `).eq('is_active', true).order('title_en');
      if (!isSuperAdmin) {
        modulesQuery = modulesQuery.in('created_by', bubbleAdminIds);
      }
      const { data: modulesData, error: modulesError } = await modulesQuery;
      if (modulesError) throw modulesError;
      setModules(modulesData || []);

      // Fetch teams (only from current admin's bubble)
      console.log('Fetching teams for current admin...');
      console.log('Current user ID:', user.id);
      console.log('Current user email:', user.email);
      
      const {
        data: teamsData,
        error: teamsError
      } = await supabase
        .from('teams')
        .select('id, name')
        .eq('created_by', user.id)
        .order('name');
      
      console.log('Teams query result:', { teamsData, teamsError });
      
      if (teamsError) {
        console.error('Teams error:', teamsError);
        
        // Try a fallback query to see all teams for debugging
        console.log('Trying fallback query to see all teams...');
        const { data: allTeams, error: allTeamsError } = await supabase
          .from('teams')
          .select('*');
        console.log('All teams (for debugging):', { allTeams, allTeamsError });
        
        setTeams([]);
      } else {
        console.log('Teams found:', teamsData?.length || 0);
        setTeams(teamsData || []);
      }

      // Fetch assignments (filtered by bubble - only assignments for modules in bubble)
      let assignmentsQuery = supabase.from('training_module_assignments').select(`
          *,
          training_modules (id, title_en, title_el, created_by),
          teams (id, name)
        `).order('assigned_at', { ascending: false });
      
      const { data: assignmentsData, error: assignmentsError } = await assignmentsQuery;
      if (assignmentsError) throw assignmentsError;
      
      // Filter assignments by bubble (only show assignments for modules in bubble)
      const filteredAssignments = isSuperAdmin 
        ? assignmentsData 
        : assignmentsData?.filter(a => bubbleAdminIds.includes(a.training_modules?.created_by)) || [];
      
      setAssignments(filteredAssignments || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };
  const saveCategory = async () => {
    if (!editingCategory) return;
    try {
      if (editingCategory.id) {
        // Update existing category
        const {
          error
        } = await supabase.from('training_categories').update({
          name_en: editingCategory.name_en,
          name_el: editingCategory.name_el,
          description_en: editingCategory.description_en,
          description_el: editingCategory.description_el,
          color: editingCategory.color,
          icon: editingCategory.icon
        }).eq('id', editingCategory.id);
        if (error) throw error;
      } else {
        // Create new category
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        const {
          error
        } = await supabase.from('training_categories').insert({
          name_en: editingCategory.name_en,
          name_el: editingCategory.name_el,
          description_en: editingCategory.description_en,
          description_el: editingCategory.description_el,
          color: editingCategory.color,
          icon: editingCategory.icon,
          created_by: user.id
        });
        if (error) throw error;
      }
      toast.success('Category saved successfully!');
      setIsDialogOpen(false);
      setEditingCategory(null);
      fetchData();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error('Failed to save category');
    }
  };
  const deleteCategory = async (categoryId: string) => {
    try {
      const {
        error
      } = await supabase.from('training_categories').delete().eq('id', categoryId);
      if (error) throw error;
      toast.success('Category deleted successfully!');
      fetchData();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Failed to delete category');
    }
  };

  const openAssignmentDetails = async (assignment: Assignment) => {
    console.log('Opening assignment details for:', assignment);
    setSelectedAssignment(assignment);
    console.log('Fetching assignment members...');
    await fetchAssignmentMembers(assignment);
    console.log('Opening dialog...');
    setIsAssignmentDetailsOpen(true);
  };

  const fetchAssignmentMembers = async (assignment: Assignment) => {
    try {
      // Get team members
      const { data: members, error: membersError } = await supabase
        .from('team_members')
        .select(`
          user_id,
          profiles!inner(id, full_name, email)
        `)
        .eq('team_id', assignment.team_id);
        
      console.log('Team members query result:', { members, error: membersError });

      if (membersError) throw membersError;
      if (!members || members.length === 0) {
        setAssignmentMembers([]);
        return;
      }

      // Get assessment forms for this module
      const { data: assessmentForms } = await supabase
        .from('assessment_forms')
        .select('id, passing_score')
        .eq('module_id', assignment.module_id);
      
      const formIds = assessmentForms?.map(f => f.id) || [];
      const passingScore = assessmentForms?.[0]?.passing_score || 70;

      // Get assessment results for all members
      const userIds = members.map(m => m.user_id);
      const { data: results } = await supabase
        .from('assessment_results')
        .select('*')
        .in('user_id', userIds)
        .in('form_id', formIds);
      
      console.log('Assessment results:', results);

      // Check for reassignments - get all assignments with reasons
      const { data: reassignments } = await supabase
        .from('module_user_assignments')
        .select('user_id, reason_for_reassignment, created_at')
        .eq('module_id', assignment.module_id)
        .in('user_id', userIds)
        .not('reason_for_reassignment', 'is', null)
        .order('created_at', { ascending: false });

      console.log('Reassignments found:', reassignments);

      // Map results to members
      const membersWithPerformance = members.map(member => {
        const userResults = results?.filter(r => r.user_id === member.user_id) || [];
        const latestResult = userResults.sort((a, b) => 
          new Date(b.completed_at).getTime() - new Date(a.completed_at).getTime()
        )[0];
        
        const hasCompleted = !!latestResult;
        const score = latestResult?.percentage_score || 0;
        const isPassing = hasCompleted && latestResult.passed;
        
        // Check if user was reassigned (has reassignment record but no completion)
        const userReassignment = reassignments?.find(r => r.user_id === member.user_id);
        const wasReassigned = !!userReassignment && !hasCompleted;
        
        return {
          user_id: member.user_id,
          profiles: member.profiles,
          hasCompleted,
          score,
          isPassing,
          passingScore,
          completedAt: latestResult?.completed_at,
          attemptCount: userResults.length,
          wasReassigned,
          reassignmentReason: userReassignment?.reason_for_reassignment
        };
      });

      setAssignmentMembers(membersWithPerformance);
    } catch (error) {
      console.error('Error fetching assignment members:', error);
      toast.error('Failed to load team member performance');
    }
  };

  const deleteAssignment = async (assignmentId: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    
    if (!confirm(language === 'en' ? 'Are you sure you want to revoke this assignment?' : 'Είστε σίγουροι ότι θέλετε να ανακαλέσετε αυτήν την ανάθεση;')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('training_module_assignments')
        .delete()
        .eq('id', assignmentId);

      if (error) throw error;
      toast.success(language === 'en' ? 'Assignment revoked successfully' : 'Η ανάθεση ανακλήθηκε επιτυχώς');
      fetchData();
    } catch (error) {
      console.error('Error deleting assignment:', error);
      toast.error(language === 'en' ? 'Failed to revoke assignment' : 'Αποτυχία ανάκλησης ανάθεσης');
    }
  };

  const reassignTraining = async (userId: string, reason: string) => {
    try {
      if (!selectedAssignment) return;

      // Delete existing completion record so user can retake
      const { error: deleteCompletionError, data: deletedCompletions } = await supabase
        .from('training_completions')
        .delete()
        .eq('user_id', userId)
        .eq('module_id', selectedAssignment.module_id)
        .select();

      console.log('Deleted completions:', deletedCompletions, 'Error:', deleteCompletionError);

      if (deleteCompletionError) {
        console.error('Error deleting completion:', deleteCompletionError);
      }

      // Delete user progress
      const { error: deleteProgressError, data: deletedProgress } = await supabase
        .from('user_module_progress')
        .delete()
        .eq('user_id', userId)
        .eq('module_id', selectedAssignment.module_id)
        .select();

      console.log('Deleted progress:', deletedProgress, 'Error:', deleteProgressError);

      if (deleteProgressError) {
        console.error('Error deleting progress:', deleteProgressError);
      }

      // Delete section progress
      const { error: deleteSectionProgressError, data: deletedSectionProgress } = await supabase
        .from('user_section_progress')
        .delete()
        .eq('user_id', userId)
        .eq('module_id', selectedAssignment.module_id)
        .select();

      console.log('Deleted section progress:', deletedSectionProgress, 'Error:', deleteSectionProgressError);

      if (deleteSectionProgressError) {
        console.error('Error deleting section progress:', deleteSectionProgressError);
      }

      // Delete assessment results
      const { data: assessmentForms } = await supabase
        .from('assessment_forms')
        .select('id')
        .eq('module_id', selectedAssignment.module_id);
      
      if (assessmentForms && assessmentForms.length > 0) {
        const formIds = assessmentForms.map(f => f.id);
        const { error: deleteResultsError } = await supabase
          .from('assessment_results')
          .delete()
          .eq('user_id', userId)
          .in('form_id', formIds);

        if (deleteResultsError) {
          console.error('Error deleting assessment results:', deleteResultsError);
        }
      }

      // Create individual reassignment
      const reassignmentData = {
        module_id: selectedAssignment.module_id,
        user_id: userId,
        assigned_by: (await supabase.auth.getUser()).data.user?.id,
        reason_for_reassignment: reason,
        due_date: selectedAssignment.due_date,
        is_mandatory: true
      };
      
      console.log('Creating reassignment:', reassignmentData);
      
      const { error: reassignError, data: reassignData } = await supabase
        .from('module_user_assignments')
        .insert(reassignmentData)
        .select();

      console.log('Reassignment created:', reassignData, 'Error:', reassignError);

      if (reassignError) throw reassignError;

      // Send reassignment notification email
      const member = assignmentMembers.find(m => m.user_id === userId);
      if (member) {
        await supabase.functions.invoke('send-email', {
          body: {
            to: member.profiles.email,
            from: 'Security Management <noreply@shiftask.app>',
            subject: `🔄 Training Reassignment: ${selectedAssignment.training_modules.title_en}`,
            html: `
              <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                <h2 style="color: #dc2626;">🔄 Training Reassignment Required</h2>
                <p>Hello ${member.profiles.full_name},</p>
                <p>You have been reassigned the training module <strong>${selectedAssignment.training_modules.title_en}</strong>.</p>
                
                <div style="background-color: #fef2f2; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #dc2626;">
                  <h3 style="color: #dc2626; margin-top: 0;">Reason for Reassignment:</h3>
                  <p style="color: #7f1d1d;">${reason}</p>
                </div>
                
                <p>Please complete this training module again to meet the required standards.</p>
                
                <div style="margin: 30px 0;">
                  <a href="https://app.shiftask.app/training" 
                     style="background-color: #dc2626; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; display: inline-block;">
                    Retake Training
                  </a>
                </div>
                
                <p style="color: #6b7280; font-size: 14px;">
                  If you have any questions, please contact your administrator.
                </p>
              </div>
            `
          }
        });
      }

      toast.success('Training reassigned successfully! Notification sent.');
      await fetchAssignmentMembers(selectedAssignment); // Refresh data
    } catch (error) {
      console.error('Error reassigning training:', error);
      toast.error('Failed to reassign training');
    }
  };

  const assignTrainingToTeam = async (moduleId: string, teamId: string, dueDate?: string, isMandatory = true) => {
    try {
      const {
        error
      } = await supabase.from('training_module_assignments').insert({
        module_id: moduleId,
        team_id: teamId,
        assigned_by: (await supabase.auth.getUser()).data.user?.id,
        due_date: dueDate,
        is_mandatory: isMandatory
      });
      if (error) throw error;
      toast.success('Training assigned to team successfully!');
      setIsAssignmentDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error assigning training:', error);
      toast.error('Failed to assign training');
    }
  };
  const addCategory = () => {
    setEditingCategory({
      id: '',
      name_en: '',
      name_el: '',
      description_en: '',
      description_el: '',
      color: '#3B82F6',
      icon: 'BookOpen'
    });
    setIsDialogOpen(true);
  };
  const editCategory = (category: TrainingCategory) => {
    setEditingCategory({
      ...category
    });
    setIsDialogOpen(true);
  };
  const addModule = () => {
    setEditingModule({
      id: '',
      title_en: '',
      title_el: '',
      description_en: '',
      description_el: '',
      category_id: '',
      content_en: '',
      content_el: '',
      duration_minutes: 30,
      difficulty_level: 'beginner',
      is_active: true,
      tags: []
    });
    setIsModuleDialogOpen(true);
  };
  const editModule = (module: TrainingModule) => {
    setEditingModule({
      ...module
    });
    setIsModuleDialogOpen(true);
  };
  const saveModule = async () => {
    if (!editingModule) return;
    try {
      if (editingModule.id) {
        // Update existing module
        const {
          error
        } = await supabase.from('training_modules').update({
          title_en: editingModule.title_en,
          title_el: editingModule.title_el,
          description_en: editingModule.description_en,
          description_el: editingModule.description_el,
          category_id: editingModule.category_id || null,
          content_en: editingModule.content_en,
          content_el: editingModule.content_el,
          duration_minutes: editingModule.duration_minutes,
          difficulty_level: editingModule.difficulty_level,
          is_active: editingModule.is_active,
          tags: editingModule.tags
        }).eq('id', editingModule.id);
        if (error) throw error;
      } else {
        // Create new module
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        const {
          error
        } = await supabase.from('training_modules').insert({
          title_en: editingModule.title_en,
          title_el: editingModule.title_el,
          description_en: editingModule.description_en,
          description_el: editingModule.description_el,
          category_id: editingModule.category_id || null,
          content_en: editingModule.content_en,
          content_el: editingModule.content_el,
          duration_minutes: editingModule.duration_minutes,
          difficulty_level: editingModule.difficulty_level,
          is_active: editingModule.is_active,
          tags: editingModule.tags,
          created_by: user.id,
          created_by_admin: user.id
        });
        if (error) throw error;
      }
      toast.success('Module saved successfully!');
      setIsModuleDialogOpen(false);
      setEditingModule(null);
      fetchData();
    } catch (error) {
      console.error('Error saving module:', error);
      toast.error('Failed to save module');
    }
  };
  const deleteModule = async (moduleId: string) => {
    try {
      const {
        error
      } = await supabase.from('training_modules').update({
        is_active: false
      }).eq('id', moduleId);
      if (error) throw error;
      toast.success('Module deleted successfully!');
      fetchData();
    } catch (error) {
      console.error('Error deleting module:', error);
      toast.error('Failed to delete module');
    }
  };
  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>;
  }
  return <div className="space-y-6 p-[30px]">
      <div>
        <h2 className="text-3xl font-bold">
          {language === 'en' ? 'Training Administration' : 'Διαχείριση Εκπαίδευσης'}
        </h2>
        <p className="text-muted-foreground">
          {language === 'en' ? 'Manage categories, assignments, and training content' : 'Διαχειριστείτε κατηγορίες, αναθέσεις και εκπαιδευτικό περιεχόμενο'}
        </p>
      </div>
      
      <Button variant="outline" onClick={() => navigate('/training')} className="w-fit">
        <ArrowLeft className="h-4 w-4 mr-2" />
        {language === 'en' ? 'Back to Training' : 'Πίσω στην Εκπαίδευση'}
      </Button>

      <Tabs defaultValue="categories" className="space-y-4">
        <TabsList>
          <TabsTrigger value="categories" className="flex items-center gap-2">
            <Tag className="h-4 w-4" />
            {language === 'en' ? 'Categories' : 'Κατηγορίες'}
          </TabsTrigger>
          <TabsTrigger value="assignments" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            {language === 'en' ? 'Assignments' : 'Αναθέσεις'}
          </TabsTrigger>
          <TabsTrigger value="modules" className="flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            {language === 'en' ? 'Modules' : 'Μοντέλα'}
          </TabsTrigger>
          <TabsTrigger value="certificates" className="flex items-center gap-2">
            <Award className="h-4 w-4" />
            {language === 'en' ? 'Certificate Templates' : 'Πρότυπα Πιστοποιητικών'}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="categories" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">
              {language === 'en' ? 'Training Categories' : 'Κατηγορίες Εκπαίδευσης'}
            </h3>
            <Button onClick={addCategory}>
              <Plus className="h-4 w-4 mr-2" />
              {language === 'en' ? 'Add Category' : 'Προσθήκη Κατηγορίας'}
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map(category => <Card key={category.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 rounded-full" style={{
                    backgroundColor: category.color
                  }} />
                      <CardTitle className="text-base">
                        {language === 'en' ? category.name_en : category.name_el}
                      </CardTitle>
                    </div>
                    <div className="flex space-x-1">
                      <Button variant="ghost" size="sm" onClick={() => editCategory(category)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteCategory(category.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    {language === 'en' ? category.description_en : category.description_el}
                  </p>
                </CardContent>
              </Card>)}
          </div>
        </TabsContent>

        <TabsContent value="assignments" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">
              {language === 'en' ? 'Training Assignments' : 'Αναθέσεις Εκπαίδευσης'}
            </h3>
            <Dialog open={isAssignmentDialogOpen} onOpenChange={setIsAssignmentDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  {language === 'en' ? 'Assign Training' : 'Ανάθεση Εκπαίδευσης'}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {language === 'en' ? 'Assign Training to Team' : 'Ανάθεση Εκπαίδευσης σε Ομάδα'}
                  </DialogTitle>
                </DialogHeader>
                <AssignmentForm modules={modules} teams={teams} onSubmit={assignTrainingToTeam} onCancel={() => setIsAssignmentDialogOpen(false)} />
              </DialogContent>
            </Dialog>
          </div>

          <div className="space-y-4">
            {assignments.map(assignment => <Card key={assignment.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => openAssignmentDetails(assignment)}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">
                        {language === 'en' ? assignment.training_modules.title_en : assignment.training_modules.title_el}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {language === 'en' ? 'Assigned to: ' : 'Ανατέθηκε σε: '}
                        {assignment.teams?.name || 'Individual User'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {language === 'en' ? 'Assigned: ' : 'Ανατέθηκε: '}
                        {new Date(assignment.assigned_at).toLocaleDateString()}
                        {assignment.due_date && <>
                            {' • '}
                            {language === 'en' ? 'Due: ' : 'Λήξη: '}
                            {new Date(assignment.due_date).toLocaleDateString()}
                          </>}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {assignment.is_mandatory && <Badge variant="destructive">
                          {language === 'en' ? 'Mandatory' : 'Υποχρεωτικό'}
                        </Badge>}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 hover:bg-destructive/10 hover:text-destructive"
                        onClick={(e) => deleteAssignment(assignment.id, e)}
                        title={language === 'en' ? 'Revoke Assignment' : 'Ανάκληση Ανάθεσης'}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                </CardContent>
              </Card>)}
          </div>

          {/* Assignment Details Dialog */}
          <Dialog open={isAssignmentDetailsOpen} onOpenChange={setIsAssignmentDetailsOpen}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {language === 'en' ? 'Assignment Details' : 'Λεπτομέρειες Ανάθεσης'}
                </DialogTitle>
              </DialogHeader>
              
              {selectedAssignment && (
                <div className="space-y-6">
                  {/* Assignment Info */}
                  <div className="bg-muted p-4 rounded-lg">
                    <h4 className="font-semibold mb-2">
                      {language === 'en' ? selectedAssignment.training_modules.title_en : selectedAssignment.training_modules.title_el}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {language === 'en' ? 'Team: ' : 'Ομάδα: '}{selectedAssignment.teams?.name}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {language === 'en' ? 'Assigned: ' : 'Ανατέθηκε: '}
                      {new Date(selectedAssignment.assigned_at).toLocaleDateString()}
                    </p>
                  </div>

                  {/* Summary Stats */}
                  <div className="grid grid-cols-4 gap-4">
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold">{assignmentMembers.length}</div>
                        <div className="text-sm text-muted-foreground">
                          {language === 'en' ? 'Total Members' : 'Σύνολο Μελών'}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-green-600">
                          {assignmentMembers.filter(m => m.hasCompleted && m.isPassing).length}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {language === 'en' ? 'Passed' : 'Πέρασαν'}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-red-600">
                          {assignmentMembers.filter(m => m.hasCompleted && !m.isPassing).length}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {language === 'en' ? 'Failed' : 'Απέτυχαν'}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-orange-600">
                          {assignmentMembers.filter(m => !m.hasCompleted && !m.wasReassigned).length}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {language === 'en' ? 'Pending' : 'Εκκρεμεί'}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-green-600">
                          {assignmentMembers.filter(m => !m.hasCompleted && m.wasReassigned).length}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {language === 'en' ? 'Reassigned' : 'Επανανατέθηκαν'}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Member List */}
                  <div>
                    <h4 className="font-semibold mb-3">
                      {language === 'en' ? 'Member Status' : 'Κατάσταση Μελών'}
                    </h4>
                    <div className="space-y-2">
                      {assignmentMembers.map(member => (
                        <Card key={member.user_id}>
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="font-medium">{member.profiles?.full_name}</div>
                                <div className="text-sm text-muted-foreground">{member.profiles?.email}</div>
                              </div>
                              
                              <div className="flex items-center gap-4">
                                {member.hasCompleted ? (
                                  <>
                                    <div className="text-right">
                                      <div className={`text-lg font-bold ${
                                        member.isPassing ? 'text-green-600' : 'text-red-600'
                                      }`}>
                                        {member.score}%
                                      </div>
                                      <div className="text-xs text-muted-foreground">
                                        {new Date(member.completedAt).toLocaleDateString()}
                                      </div>
                                    </div>
                                    <Badge variant={member.isPassing ? 'default' : 'destructive'}>
                                      {member.isPassing 
                                        ? (language === 'en' ? 'Passed' : 'Πέρασε')
                                        : (language === 'en' ? 'Failed' : 'Απέτυχε')
                                      }
                                    </Badge>
                                    {!member.isPassing && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => reassignTraining(member.user_id, 'Failed assessment')}
                                      >
                                        {language === 'en' ? 'Reassign' : 'Επανανάθεση'}
                                      </Button>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    {member.wasReassigned ? (
                                      <Badge variant="default" className="bg-green-600 hover:bg-green-700">
                                        {language === 'en' ? 'Reassigned' : 'Επανανατέθηκε'}
                                      </Badge>
                                    ) : (
                                      <Badge variant="secondary">
                                        {language === 'en' ? 'Pending' : 'Εκκρεμεί'}
                                      </Badge>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => reassignTraining(member.user_id, 'Reminder - Please complete training')}
                                    >
                                      {language === 'en' ? 'Remind' : 'Υπενθύμιση'}
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="modules" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">
              {language === 'en' ? 'Training Modules' : 'Εκπαιδευτικά Μοντέλα'}
            </h3>
            <Dialog open={isModuleDialogOpen} onOpenChange={setIsModuleDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={addModule}>
                  <Plus className="h-4 w-4 mr-2" />
                  {language === 'en' ? 'Add Module' : 'Προσθήκη Μοντέλου'}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingModule?.id ? language === 'en' ? 'Edit Module' : 'Επεξεργασία Μοντέλου' : language === 'en' ? 'Add Module' : 'Προσθήκη Μοντέλου'}
                  </DialogTitle>
                </DialogHeader>
                <ModuleForm module={editingModule} categories={categories} onSubmit={saveModule} onCancel={() => setIsModuleDialogOpen(false)} onChange={module => setEditingModule(module)} />
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {modules.map(module => <Card key={module.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">
                      {language === 'en' ? module.title_en : module.title_el}
                    </CardTitle>
                    <div className="flex space-x-1">
                      <Button variant="ghost" size="sm" onClick={() => editModule(module)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteModule(module.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {module.training_categories && <Badge style={{
                  backgroundColor: module.training_categories.color + '20',
                  color: module.training_categories.color
                }}>
                        {language === 'en' ? module.training_categories.name_en : module.training_categories.name_el}
                      </Badge>}
                    <p className="text-sm text-muted-foreground">
                      {language === 'en' ? module.description_en : module.description_el}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {module.duration_minutes && <span>{module.duration_minutes} min</span>}
                      {module.difficulty_level && <Badge variant="outline" className="text-xs">
                          {module.difficulty_level}
                        </Badge>}
                    </div>
                  </div>
                </CardContent>
              </Card>)}
          </div>
        </TabsContent>

        <TabsContent value="certificates" className="space-y-4">
          <CertificateTemplatesManager />
        </TabsContent>
      </Tabs>

      {/* Category Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCategory?.id ? language === 'en' ? 'Edit Category' : 'Επεξεργασία Κατηγορίας' : language === 'en' ? 'Add Category' : 'Προσθήκη Κατηγορίας'}
            </DialogTitle>
          </DialogHeader>
          
          {editingCategory && <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name_en">Name (English)</Label>
                  <Input id="name_en" value={editingCategory.name_en} onChange={e => setEditingCategory({
                ...editingCategory,
                name_en: e.target.value
              })} />
                </div>
                <div>
                  <Label htmlFor="name_el">Name (Greek)</Label>
                  <Input id="name_el" value={editingCategory.name_el} onChange={e => setEditingCategory({
                ...editingCategory,
                name_el: e.target.value
              })} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="description_en">Description (English)</Label>
                  <Textarea id="description_en" value={editingCategory.description_en} onChange={e => setEditingCategory({
                ...editingCategory,
                description_en: e.target.value
              })} />
                </div>
                <div>
                  <Label htmlFor="description_el">Description (Greek)</Label>
                  <Textarea id="description_el" value={editingCategory.description_el} onChange={e => setEditingCategory({
                ...editingCategory,
                description_el: e.target.value
              })} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="color">Color</Label>
                  <Input id="color" type="color" value={editingCategory.color} onChange={e => setEditingCategory({
                ...editingCategory,
                color: e.target.value
              })} />
                </div>
                <div>
                  <Label htmlFor="icon">Icon</Label>
                  <Select value={editingCategory.icon} onValueChange={value => setEditingCategory({
                ...editingCategory,
                icon: value
              })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="BookOpen">Book Open</SelectItem>
                      <SelectItem value="Shield">Shield</SelectItem>
                      <SelectItem value="Computer">Computer</SelectItem>
                      <SelectItem value="AlertTriangle">Alert Triangle</SelectItem>
                      <SelectItem value="Key">Key</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={saveCategory}>
                  Save Category
                </Button>
              </div>
            </div>}
        </DialogContent>
      </Dialog>
    </div>;
};
const AssignmentForm = ({
  modules,
  teams,
  onSubmit,
  onCancel
}: {
  modules: TrainingModule[];
  teams: Team[];
  onSubmit: (moduleId: string, teamId: string, dueDate?: string, isMandatory?: boolean) => void;
  onCancel: () => void;
}) => {
  const {
    language
  } = useLanguage();
  const [selectedModule, setSelectedModule] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [isMandatory, setIsMandatory] = useState(true);
  const handleSubmit = () => {
    if (!selectedModule || !selectedTeam) {
      toast.error('Please select both module and team');
      return;
    }
    onSubmit(selectedModule, selectedTeam, dueDate || undefined, isMandatory);
  };
  return <div className="space-y-4">
      <div>
        <Label>Training Module</Label>
        <Select value={selectedModule} onValueChange={setSelectedModule}>
          <SelectTrigger>
            <SelectValue placeholder="Select a training module" />
          </SelectTrigger>
          <SelectContent>
            {modules.map(module => <SelectItem key={module.id} value={module.id}>
                {language === 'en' ? module.title_en : module.title_el}
              </SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Team</Label>
        <Select value={selectedTeam} onValueChange={setSelectedTeam}>
          <SelectTrigger>
            <SelectValue placeholder="Select a team" />
          </SelectTrigger>
          <SelectContent>
            {teams.map(team => <SelectItem key={team.id} value={team.id}>
                {team.name}
              </SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Due Date (Optional)</Label>
        <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
      </div>

      <div className="flex justify-end space-x-2">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSubmit}>
          Assign Training
        </Button>
      </div>
    </div>;
};